import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MakeupClass, MakeupService } from '@/services/makeupService';
import { ClassService } from '@/services/classService';
import { Class } from '@/types/models';
import { Plus, Edit, Trash2, Calendar as CalendarIcon, Clock, Users } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { studentService } from '@/services/studentService';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { supabase } from '@/lib/supabase';

// Add this type for available classes with extra fields
type AvailableClass = Class & {
  current_enrollment: number;
  next_schedule: any; // You can refine this type if you have a Schedule type
};

const MakeupClasses = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState<Class[]>([]);
  const [makeupClasses, setMakeupClasses] = useState<MakeupClass[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [maxStudents, setMaxStudents] = useState<number>(5);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allBookings, setAllBookings] = useState<any[]>([]);
  const [isBookingsLoading, setIsBookingsLoading] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<AvailableClass[]>([]);
  const [isAvailableLoading, setIsAvailableLoading] = useState(false);
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [bookingClass, setBookingClass] = useState<AvailableClass | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedClassForConfig, setSelectedClassForConfig] = useState<AvailableClass | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState('none');
  const [classPolicies, setClassPolicies] = useState<Record<string, 'flexible' | 'curated' | 'none'>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      loadClasses();
      loadMakeupClasses();
      loadAllBookings();
      loadAvailableClasses();
    }
  }, [user]);

  // Pre-fill fields when dialog opens and classes are loaded
  useEffect(() => {
    if (dialogOpen && classes.length > 0) {
      const firstClass = classes[0];
      setSelectedClassId(firstClass.id);
      // Prefer event_start_time/event_end_time, else use first schedule
      let st = firstClass.event_start_time || '';
      let et = firstClass.event_end_time || '';
      if ((!st || !et) && firstClass.schedules && firstClass.schedules.length > 0) {
        st = firstClass.schedules[0].start_time;
        et = firstClass.schedules[0].end_time;
      }
      setStartTime(st || '');
      setEndTime(et || '');
      setSelectedDate(new Date());
    }
  }, [dialogOpen, classes]);

  // Update times when class changes
  useEffect(() => {
    if (selectedClassId && classes.length > 0) {
      const cls = classes.find(c => c.id === selectedClassId);
      if (cls) {
        let st = cls.event_start_time || '';
        let et = cls.event_end_time || '';
        if ((!st || !et) && cls.schedules && cls.schedules.length > 0) {
          st = cls.schedules[0].start_time;
          et = cls.schedules[0].end_time;
        }
        setStartTime(st || '');
        setEndTime(et || '');
      }
    }
  }, [selectedClassId, classes]);

  const loadClasses = async () => {
    try {
      const orgId = user?.user_metadata?.organization_id;
      if (!orgId) {
        toast.error('Missing organization ID. Please log in again.');
        return;
      }
      const result = await ClassService.getClasses(orgId);
      setClasses(result);
    } catch (error) {
      console.error('Error loading classes:', error);
      toast.error('Failed to load classes');
    }
  };

  const loadMakeupClasses = async () => {
    setIsLoading(true);
    try {
      // We'll use getAllAvailableMakeupClasses even though it's designed for the parent portal
      // In a production app, you might want a separate admin method
      const result = await MakeupService.getAllAvailableMakeupClasses();
      setMakeupClasses(result);
    } catch (error) {
      console.error('Error loading makeup classes:', error);
      toast.error('Failed to load makeup classes');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllBookings = async () => {
    setIsBookingsLoading(true);
    try {
      const orgId = user?.user_metadata?.organization_id;
      if (!orgId) return;
      const result = await MakeupService.getAllMakeupBookings(orgId);
      setAllBookings(result);
    } catch (error) {
      console.error('Error loading all makeup bookings:', error);
    } finally {
      setIsBookingsLoading(false);
    }
  };

  const loadAvailableClasses = async () => {
    setIsAvailableLoading(true);
    try {
      const orgId = user?.user_metadata?.organization_id;
      if (!orgId) return;
      
      // Fetch classes with their schedules in one query
      const { data: classesData, error } = await supabase
        .from('classes')
        .select(`
          *,
          schedules:class_schedules!class_schedules_class_id_fkey(*)
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const allClasses = (classesData || []).map(item => ({
        ...item,
        description: item.description || '',
        is_active: item.is_active !== undefined ? item.is_active : true,
        is_event: item.is_event !== undefined ? item.is_event : false,
      })) as Class[];
      
      const filtered: AvailableClass[] = [];
      const policies: Record<string, 'flexible' | 'curated' | 'none'> = {};
      
      for (const cls of allClasses) {
        // Only consider active classes with max_students defined
        if (!cls.is_active || !cls.max_students) {
          continue;
        }
        
        // Extract makeup policy if it exists (will be undefined/null if not set)
        const makeupPolicy = (cls as any).makeup_policy;
        if (makeupPolicy && (makeupPolicy === 'flexible' || makeupPolicy === 'curated')) {
          policies[cls.id] = makeupPolicy;
        } else {
          policies[cls.id] = 'none';
        }
        
        // Get current enrollment count
        const enrollments = await ClassService.getClassEnrollments(cls.id);
        const currentCount = enrollments.filter(e => e.status === 'active').length;
        
        // Check if class has available spots
        if (currentCount < cls.max_students) {
          // For next_schedule, use the first available schedule or create a placeholder
          const nextSchedule = cls.schedules && cls.schedules.length > 0 
            ? {
                ...cls.schedules[0],
                date: 'Next occurrence', // Placeholder since schedules are recurring
              }
            : {
                weekday: 1,
                start_time: 'TBD',
                end_time: 'TBD',
                date: 'TBD'
              };
          
          filtered.push({ 
            ...cls, 
            current_enrollment: currentCount, 
            next_schedule: nextSchedule 
          });
        }
      }
      
      setAvailableClasses(filtered);
      setClassPolicies(policies); // Load existing policies from database
    } catch (error) {
      console.error('Error loading available classes:', error);
      toast.error('Failed to load available classes');
    } finally {
      setIsAvailableLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const orgId = user?.user_metadata?.organization_id;
      if (!orgId) return;
      const all = await studentService.getAllStudentsForOrganization(orgId);
      setStudents(all);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedClassId || !startTime || !endTime) {
      toast.error('Please fill out all required fields');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const makeupClass = {
        class_id: selectedClassId,
        date: selectedDate.toISOString().split('T')[0],
        start_time: startTime,
        end_time: endTime,
        max_students: maxStudents,
        current_students: 0,
        organization_id: user?.user_metadata?.organization_id
      };
      
      await MakeupService.createMakeupClass(makeupClass);
      
      // Reset form and reload data
      setSelectedDate(undefined);
      setSelectedClassId('');
      setStartTime('');
      setEndTime('');
      setMaxStudents(5);
      setDialogOpen(false);
      
      // Reload the makeup classes
      loadMakeupClasses();
      
    } catch (error) {
      console.error('Error creating makeup class:', error);
      toast.error('Failed to create makeup class');
    } finally {
      setIsCreating(false);
    }
  };

  // Utility to format time for display
  const formatTime = (timeStr: string) => {
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch {
      return timeStr;
    }
  };

  // Custom DatePicker that disables past dates
  const CustomDatePicker = () => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today;
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  };

  // Open booking modal
  const openBookModal = (cls: AvailableClass) => {
    setBookingClass(cls);
    setBookModalOpen(true);
    setSelectedStudentId('');
    loadStudents();
  };

  // Handle booking confirm
  const handleBookMakeup = async () => {
    if (!selectedStudentId || !bookingClass) {
      toast.error('Please select a student');
      return;
    }
    setIsBooking(true);
    try {
      // Create a new makeup_bookings record (no absence, admin flow)
      const orgId = user?.user_metadata?.organization_id;
      
      // Only pass real date/time values, not placeholders
      const scheduleDate = bookingClass.next_schedule && 
                          bookingClass.next_schedule.date !== 'TBD' && 
                          bookingClass.next_schedule.date !== 'Next occurrence' ? 
                          bookingClass.next_schedule.date : undefined;
      
      const startTime = bookingClass.next_schedule && 
                       bookingClass.next_schedule.start_time !== 'TBD' ? 
                       bookingClass.next_schedule.start_time : undefined;
      
      const endTime = bookingClass.next_schedule && 
                     bookingClass.next_schedule.end_time !== 'TBD' ? 
                     bookingClass.next_schedule.end_time : undefined;
      
      await MakeupService.adminBookMakeup({
        student_id: selectedStudentId,
        class_id: bookingClass.id,
        organization_id: orgId,
        date: scheduleDate,
        start_time: startTime,
        end_time: endTime,
      });
      toast.success('Makeup booked successfully');
      setBookModalOpen(false);
      setBookingClass(null);
      setSelectedStudentId('');
      loadAllBookings();
      loadAvailableClasses();
    } catch (error) {
      console.error('Error booking makeup:', error);
      toast.error('Failed to book makeup');
    } finally {
      setIsBooking(false);
    }
  };

  // Save policy configuration to database
  const savePolicyConfiguration = async () => {
    if (!selectedClassForConfig || !selectedPolicy) {
      toast.error('Please select a policy');
      return;
    }

    // Validate curated dates selection
    if (selectedPolicy === 'curated' && (!selectedClassForConfig.schedules || selectedClassForConfig.schedules.length === 0)) {
      toast.error('Cannot set curated dates for a class without a regular schedule. Please add a weekly schedule first.');
      return;
    }

    setIsSaving(true);
    try {
      // Use real ClassService to update the class with makeup_policy
      const makeupPolicyValue: 'flexible' | 'curated' | null = selectedPolicy === 'none' ? null : selectedPolicy as 'flexible' | 'curated';
      const updateData = {
        makeup_policy: makeupPolicyValue
      };
      
      const updatedClass = await ClassService.updateClass(selectedClassForConfig.id, updateData);
      
      if (updatedClass) {
        // Update local state to reflect the change
        setClassPolicies(prev => ({
          ...prev,
          [selectedClassForConfig.id]: selectedPolicy as 'flexible' | 'curated' | 'none'
        }));
        
        // Show appropriate success message
        const policyNames = {
          flexible: 'Flexible scheduling',
          curated: 'Curated dates', 
          none: 'No makeup policy (disabled)'
        };
        
        toast.success(`${policyNames[selectedPolicy]} policy saved for ${selectedClassForConfig?.name}${selectedPolicy === 'none' ? '. Existing bookings remain valid.' : ''}`);
        setConfigModalOpen(false);
        setSelectedClassForConfig(null);
        setSelectedPolicy('none');
      } else {
        throw new Error('Failed to update class');
      }
      
    } catch (error) {
      console.error('Error saving makeup policy:', error);
      toast.error('Failed to save makeup policy. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <StandardPageHeader 
        title="Makeup Classes"
        subtitle="Manage makeup class options for students"
        icon={CalendarIcon}
      />
      
      <PageContainer maxWidth="full">
      <Tabs defaultValue="upcoming">
        <TabsList className="mb-4">
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="past">Past</TabsTrigger>
          <TabsTrigger value="all">All Makeup Reservations</TabsTrigger>
          <TabsTrigger value="available">Available Classes for Makeups</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming">
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Makeup Classes</CardTitle>
              <CardDescription>Classes available for makeup sessions</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : makeupClasses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="mb-2">No upcoming makeup classes found</div>
                </div>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Class</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Capacity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {makeupClasses
                        .filter(mc => new Date(mc.date) >= new Date())
                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                        .map((makeup) => (
                          <TableRow key={makeup.id}>
                            <TableCell className="font-medium">
                              {makeup.class?.name || 'Unknown class'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                                {formatDate(makeup.date)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                                {formatTime(makeup.start_time)} - {formatTime(makeup.end_time)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                                {makeup.current_students} / {makeup.max_students}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="past">
          <Card>
            <CardHeader>
              <CardTitle>Past Makeup Classes</CardTitle>
              <CardDescription>Makeup classes that have already occurred</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Class</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Capacity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {makeupClasses
                        .filter(mc => new Date(mc.date) < new Date())
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .map((makeup) => (
                          <TableRow key={makeup.id}>
                            <TableCell className="font-medium">
                              {makeup.class?.name || 'Unknown class'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                                {formatDate(makeup.date)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                                {formatTime(makeup.start_time)} - {formatTime(makeup.end_time)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                                {makeup.current_students} / {makeup.max_students}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card>
            <CardHeader>
              <CardTitle>All Makeup Reservations</CardTitle>
              <CardDescription>All confirmed and scheduled makeup bookings</CardDescription>
            </CardHeader>
            <CardContent>
              {isBookingsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Confirmed By</TableHead>
                        <TableHead>Created At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allBookings.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No makeup bookings found
                          </TableCell>
                        </TableRow>
                      ) : (
                        allBookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell>{booking.student?.first_name} {booking.student?.last_name}</TableCell>
                            <TableCell>{booking.makeup_class?.class?.name || 'Unknown'}</TableCell>
                            <TableCell>{booking.makeup_class?.date || ''}</TableCell>
                            <TableCell>{booking.makeup_class ? `${booking.makeup_class.start_time} - ${booking.makeup_class.end_time}` : ''}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                booking.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {booking.notes ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="truncate max-w-[120px] inline-block align-middle cursor-pointer">{booking.notes}</span>
                                    </TooltipTrigger>
                                    <TooltipContent>{booking.notes}</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell>
                              {booking.created_by_profile ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="truncate max-w-[120px] inline-block align-middle cursor-pointer">
                                        {booking.created_by_profile.first_name || ''} {booking.created_by_profile.last_name || ''}
                                        {!(booking.created_by_profile.first_name || booking.created_by_profile.last_name) && booking.created_by_profile.email ? booking.created_by_profile.email : ''}
                                        {!(booking.created_by_profile.first_name || booking.created_by_profile.last_name || booking.created_by_profile.email) ? booking.created_by : ''}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {booking.created_by_profile.first_name || booking.created_by_profile.last_name ? (
                                        <>
                                          {booking.created_by_profile.first_name} {booking.created_by_profile.last_name}<br />
                                          {booking.created_by_profile.email && <>{booking.created_by_profile.email}<br /></>}
                                          User ID: {booking.created_by}
                                        </>
                                      ) : booking.created_by_profile.email ? (
                                        <>
                                          {booking.created_by_profile.email}<br />
                                          User ID: {booking.created_by}
                                        </>
                                      ) : (
                                        <>User ID: {booking.created_by}</>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell>{booking.created_at ? new Date(booking.created_at).toLocaleString() : ''}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="available">
          <Card>
            <CardHeader>
              <CardTitle>Makeup Class Policies</CardTitle>
              <CardDescription>Configure how students can book makeup sessions for each class</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isAvailableLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : availableClasses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="mb-2">No active classes found</div>
                  <div className="text-sm">Create active classes with enrollment capacity to configure makeup policies</div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Policy Explanation - Now at Top */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-3">Makeup Policy Options:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="bg-white p-3 rounded border">
                        <div className="font-semibold text-green-700 mb-1">📅 Flexible Scheduling</div>
                        <div className="text-gray-700">Parents can book into any future regular class session with available spots</div>
                        <div className="text-xs text-gray-500 mt-1">Best for: Ongoing classes, maximum flexibility</div>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <div className="font-semibold text-purple-700 mb-1">🎯 Curated Dates</div>
                        <div className="text-gray-700">You pre-select specific dates/times from your regular schedule for makeups</div>
                        <div className="text-xs text-gray-500 mt-1">Best for: Controlled capacity, special focus sessions</div>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <div className="font-semibold text-red-700 mb-1">🚫 No Makeup</div>
                        <div className="text-gray-700">Disable makeup bookings while preserving existing confirmed bookings</div>
                        <div className="text-xs text-gray-500 mt-1">Best for: Specialized classes, capacity-limited sessions</div>
                      </div>
                    </div>
                  </div>

                  {/* Class List */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Configure makeup policies for your classes:</h3>
                    <div className="grid gap-3">
                      {availableClasses.map((cls) => (
                        <div 
                          key={cls.id} 
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{cls.name}</div>
                            <div className="text-sm text-gray-600 flex items-center gap-4">
                              {cls.level && (
                                <span>Level: {cls.level}</span>
                              )}
                              <span>
                                {cls.current_enrollment}/{cls.max_students} enrolled
                              </span>
                              <span className="text-green-600 font-medium">
                                {cls.max_students - cls.current_enrollment} spots available
                              </span>
                              {/* Show schedule status */}
                              {(!cls.schedules || cls.schedules.length === 0) && (
                                <span className="text-amber-600 text-xs bg-amber-50 px-2 py-1 rounded">
                                  ⚠️ No schedule
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Current Policy Status Badge */}
                            <div className={cn(
                              "px-3 py-1 rounded-md text-sm font-medium",
                              classPolicies[cls.id] === 'flexible' ? 'bg-green-100 text-green-800' :
                              classPolicies[cls.id] === 'curated' ? 'bg-purple-100 text-purple-800' :
                              classPolicies[cls.id] === 'none' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-700'
                            )}>
                              {classPolicies[cls.id] === 'flexible' ? '📅 Flexible Scheduling' : 
                               classPolicies[cls.id] === 'curated' ? '🎯 Curated Dates' : 
                               classPolicies[cls.id] === 'none' ? '🚫 No Makeup' :
                               'No Policy Set'}
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => {
                                setSelectedClassForConfig(cls);
                                const currentPolicy = classPolicies[cls.id] || 'none';
                                // If current policy is curated but class has no schedules, reset to none
                                if (currentPolicy === 'curated' && (!cls.schedules || cls.schedules.length === 0)) {
                                  setSelectedPolicy('none');
                                } else {
                                  setSelectedPolicy(currentPolicy);
                                }
                                setConfigModalOpen(true);
                              }}
                            >
                              Configure Policy
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Class Configuration Modal */}
          <Dialog open={configModalOpen} onOpenChange={setConfigModalOpen}>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Configure Makeup Policy: {selectedClassForConfig?.name}</DialogTitle>
                <DialogDescription>
                  Set up how students can book makeup sessions for this class
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-6">
                {/* Policy Selection */}
                <div>
                  <Label className="text-base font-medium">Select Makeup Policy</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                    <div 
                      className={cn(
                        "p-4 border-2 rounded-lg cursor-pointer transition-all",
                        selectedPolicy === 'flexible' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                      )}
                      onClick={() => setSelectedPolicy('flexible')}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input 
                          type="radio" 
                          checked={selectedPolicy === 'flexible'} 
                          onChange={() => setSelectedPolicy('flexible')}
                          className="text-green-600"
                        />
                        <span className="font-semibold text-green-700">📅 Flexible Scheduling</span>
                      </div>
                      <p className="text-sm text-gray-600">Parents can join any future regular class session</p>
                    </div>
                    
                    <div 
                      className={cn(
                        "p-4 border-2 rounded-lg transition-all",
                        // Disable if class has no schedules
                        !selectedClassForConfig?.schedules || selectedClassForConfig.schedules.length === 0 ? 
                          'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' :
                        selectedPolicy === 'curated' ? 'border-purple-500 bg-purple-50 cursor-pointer' : 'border-gray-200 hover:border-purple-300 cursor-pointer'
                      )}
                      onClick={() => {
                        if (selectedClassForConfig?.schedules && selectedClassForConfig.schedules.length > 0) {
                          setSelectedPolicy('curated');
                        }
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input 
                          type="radio" 
                          checked={selectedPolicy === 'curated'} 
                          onChange={() => {
                            if (selectedClassForConfig?.schedules && selectedClassForConfig.schedules.length > 0) {
                              setSelectedPolicy('curated');
                            }
                          }}
                          disabled={!selectedClassForConfig?.schedules || selectedClassForConfig.schedules.length === 0}
                          className="text-purple-600"
                        />
                        <span className={cn(
                          "font-semibold",
                          !selectedClassForConfig?.schedules || selectedClassForConfig.schedules.length === 0 ? 
                            'text-gray-500' : 'text-purple-700'
                        )}>
                          🎯 Curated Dates
                        </span>
                      </div>
                      <p className={cn(
                        "text-sm",
                        !selectedClassForConfig?.schedules || selectedClassForConfig.schedules.length === 0 ? 
                          'text-gray-500' : 'text-gray-600'
                      )}>
                        You choose specific dates/times for makeups
                      </p>
                      
                      {/* Show warning if no schedules */}
                      {(!selectedClassForConfig?.schedules || selectedClassForConfig.schedules.length === 0) && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                          ⚠️ This class has no regular schedule. Add a weekly schedule first to enable curated dates.
                        </div>
                      )}
                    </div>

                    <div 
                      className={cn(
                        "p-4 border-2 rounded-lg cursor-pointer transition-all",
                        selectedPolicy === 'none' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'
                      )}
                      onClick={() => setSelectedPolicy('none')}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input 
                          type="radio" 
                          checked={selectedPolicy === 'none'} 
                          onChange={() => setSelectedPolicy('none')}
                          className="text-red-600"
                        />
                        <span className="font-semibold text-red-700">🚫 No Makeup</span>
                      </div>
                      <p className="text-sm text-gray-600">Disable makeup bookings for this class</p>
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        ℹ️ Existing makeup bookings will remain valid, but new bookings will be prevented.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Configuration based on selected policy */}
                {selectedPolicy === 'flexible' && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="font-medium text-green-900 mb-2">Flexible Scheduling Setup</h4>
                    <p className="text-sm text-green-800 mb-3">
                      With flexible scheduling, parents will be able to book into any upcoming regular class session for "{selectedClassForConfig?.name}" 
                      that has available enrollment spots.
                    </p>
                    <div className="text-sm text-green-700">
                      <div className="mb-1">• Parents see next 4-6 upcoming sessions</div>
                      <div className="mb-1">• Real-time availability checking</div>
                      <div>• Automatic capacity management</div>
                    </div>
                  </div>
                )}

                {selectedPolicy === 'curated' && (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-2">Curated Dates Setup</h4>
                    <p className="text-sm text-purple-800 mb-3">
                      Select which regular class sessions you want to make available for makeup bookings:
                    </p>
                    
                    {/* Show next few scheduled sessions for selection */}
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedClassForConfig?.schedules && selectedClassForConfig.schedules.length > 0 ? (
                        (() => {
                          const schedule = selectedClassForConfig.schedules[0];
                          const today = new Date();
                          const sessions = [];
                          
                          // Generate next 8 sessions
                          for (let i = 0; i < 56; i++) { // Check 8 weeks ahead
                            const date = new Date(today);
                            date.setDate(today.getDate() + i);
                            if (date.getDay() === schedule.weekday) {
                              sessions.push(date);
                              if (sessions.length >= 8) break;
                            }
                          }
                          
                          return sessions.map((date, index) => (
                            <div key={index} className="flex items-center gap-3 p-2 bg-white rounded border">
                              <input type="checkbox" className="text-purple-600" />
                              <span className="flex-1">
                                {date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} • 
                                {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                              </span>
                              <span className="text-xs text-gray-500">Regular session</span>
                            </div>
                          ));
                        })()
                      ) : (
                        <div className="text-sm text-purple-700">No regular schedule found for this class.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setConfigModalOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button 
                  onClick={savePolicyConfiguration}
                  disabled={
                    !selectedPolicy || 
                    isSaving ||
                    // Disable if trying to save curated for class without schedules
                    (selectedPolicy === 'curated' && (!selectedClassForConfig?.schedules || selectedClassForConfig.schedules.length === 0))
                  }
                >
                  {isSaving ? 'Saving...' : 'Save Policy'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Enhanced Book Makeup Modal */}
          <Dialog open={bookModalOpen} onOpenChange={setBookModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Manual Makeup Booking</DialogTitle>
                <DialogDescription>
                  Book {bookingClass?.name} for a specific student and date
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div>
                  <Label htmlFor="student">Select Student</Label>
                  <Select value={selectedStudentId} onValueChange={setSelectedStudentId} disabled={isBooking}>
                    <SelectTrigger>
                      <SelectValue placeholder={students.length === 0 ? 'No students found' : 'Select a student'} />
                    </SelectTrigger>
                    <SelectContent>
                      {students.length === 0 ? (
                        <SelectItem value="none" disabled>No students found</SelectItem>
                      ) : (
                        students.map((student) => (
                          <SelectItem key={student.id} value={student.id}>
                            {student.first_name} {student.last_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Selection for Manual Booking */}
                <div>
                  <Label>Makeup Date & Time</Label>
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg border">
                    <div className="text-sm font-medium text-blue-800 mb-2">Next Regular Sessions:</div>
                    {bookingClass?.schedules && bookingClass.schedules.length > 0 ? (
                      <div className="space-y-2">
                        {/* Calculate next 3 occurrences */}
                        {(() => {
                          const schedule = bookingClass.schedules[0];
                          const today = new Date();
                          const nextDates = [];
                          
                          for (let i = 0; i < 21; i++) { // Check next 3 weeks
                            const date = new Date(today);
                            date.setDate(today.getDate() + i);
                            if (date.getDay() === schedule.weekday) {
                              nextDates.push(date);
                              if (nextDates.length >= 3) break;
                            }
                          }
                          
                          return nextDates.map((date, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <span>
                                {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} • 
                                {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                              </span>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs">
                                Select This
                              </Button>
                            </div>
                          ));
                        })()}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">No regular schedule available</div>
                    )}
                    
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <Button size="sm" variant="ghost" className="text-xs">
                        + Custom Date/Time
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBookModalOpen(false)} disabled={isBooking}>
                  Cancel
                </Button>
                <Button onClick={handleBookMakeup} disabled={!selectedStudentId || isBooking}>
                  {isBooking ? 'Booking...' : 'Book Makeup'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
      </PageContainer>
    </div>
  );
};

export default MakeupClasses; 